import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import type { AvailableUnitGroup } from '@/components/public/units';
import type { PublishedUnitSummary } from '@/lib/types/publicProperty';
import { formatPropertyAddress, formatUnitLabel } from '@/lib/utils/propertyFormatting';
import { resolveAvailabilityMapCoordinates } from '@/routes/publicProperties/availabilitiesMapLocations';
import { AvailabilitiesMap, type AvailabilitiesMapPoint } from './AvailabilitiesMap';
import { AvailabilitiesMapPanel } from './AvailabilitiesMapPanel';
import { isAllianceGroup } from './availabilitiesMapListHelpers';
import type { AvailabilitiesAddressEntry } from './availabilitiesMapListTypes';
import type {
  AvailabilitiesMapPanelContent,
  AvailabilitiesMapUnitRowContent,
  AvailabilitiesQuickViewContent,
} from '@/lib/types/siteSettings';

interface AvailabilitiesMapListContent {
  mapPanel: AvailabilitiesMapPanelContent;
  mapUnitRow: AvailabilitiesMapUnitRowContent;
  quickView: AvailabilitiesQuickViewContent;
  availabilityLabels: { immediateLabel: string; negotiableLabel: string };
}

export function AvailabilitiesMapList({
  groups,
  content,
}: {
  groups: AvailableUnitGroup[];
  content: AvailabilitiesMapListContent;
}): JSX.Element {
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [mapFocusRequest, setMapFocusRequest] = useState(0);
  const previousSelectedPropertyIdRef = useRef<string | null>(null);

  const addressEntries: AvailabilitiesAddressEntry[] = useMemo(() => {
    return groups.map((group) => ({
      group,
      coordinates: resolveAvailabilityMapCoordinates(group.property),
      addressLabel: formatPropertyAddress(group.property.address),
      emphasis: isAllianceGroup(group.property),
    }));
  }, [groups]);

  const points: AvailabilitiesMapPoint[] = useMemo(() => {
    return addressEntries
      .map((entry) => {
        if (!entry.coordinates) {
          return null;
        }
        const { lat, lng } = entry.coordinates;
        return {
          id: entry.group.property.id,
          lat,
          lng,
          label: entry.addressLabel,
          unitCount: entry.group.units.length,
          emphasis: entry.emphasis,
        } satisfies AvailabilitiesMapPoint;
      })
      .filter(Boolean) as AvailabilitiesMapPoint[];
  }, [addressEntries]);

  const selectedGroup = useMemo(() => {
    if (selectedPropertyId === null) {
      return null;
    }
    return groups.find((group) => group.property.id === selectedPropertyId) ?? null;
  }, [groups, selectedPropertyId]);

  const selectedUnit: PublishedUnitSummary | null = useMemo(() => {
    if (!selectedGroup || selectedUnitId === null) {
      return null;
    }
    return selectedGroup.units.find((unit) => unit.id === selectedUnitId) ?? null;
  }, [selectedGroup, selectedUnitId]);

  const selectionLabel = useMemo(() => {
    if (selectedUnit && selectedGroup) {
      return `Selected ${formatUnitLabel(selectedUnit)} at ${formatPropertyAddress(
        selectedGroup.property.address,
      )}.`;
    }
    if (selectedGroup) {
      return `Selected ${formatPropertyAddress(selectedGroup.property.address)}.`;
    }
    return 'No address selected.';
  }, [selectedGroup, selectedUnit]);

  useEffect(() => {
    if (selectedPropertyId === null) {
      return;
    }
    if (!selectedGroup) {
      setSelectedPropertyId(null);
      setSelectedUnitId(null);
    }
  }, [selectedGroup, selectedPropertyId]);

  useEffect(() => {
    if (selectedGroup === null || selectedPropertyId === null) {
      previousSelectedPropertyIdRef.current = selectedPropertyId;
      return;
    }

    const previousSelected = previousSelectedPropertyIdRef.current;
    previousSelectedPropertyIdRef.current = selectedPropertyId;
    if (previousSelected === selectedPropertyId) {
      return;
    }

    if (selectedGroup.units.length === 1) {
      setSelectedUnitId(selectedGroup.units[0].id);
      return;
    }

    setSelectedUnitId(null);
  }, [selectedGroup, selectedPropertyId]);

  useEffect(() => {
    if (selectedGroup === null || selectedUnitId === null) {
      return;
    }
    if (selectedUnit === null) {
      setSelectedUnitId(null);
    }
  }, [selectedGroup, selectedUnit, selectedUnitId]);

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-stretch">
      <div className="flex flex-col gap-2">
        <a
          href="#availabilities-map-panel"
          className="sr-only focus:not-sr-only focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-xs focus:font-semibold focus:text-brand-primary focus:shadow-md"
        >
          Skip map and jump to the address list
        </a>
        <p className="sr-only" aria-live="polite">
          {selectionLabel}
        </p>
        <div className="h-[42vh] max-h-[520px] min-h-[320px] w-full lg:h-[min(72vh,760px)] lg:max-h-none">
          <AvailabilitiesMap
            points={points}
            selectedPointId={selectedPropertyId}
            focusRequest={mapFocusRequest}
            onSelectPoint={(propertyId) => {
              setSelectedPropertyId(propertyId);
            }}
            onUserInteraction={() => {
              setSelectedPropertyId(null);
              setSelectedUnitId(null);
            }}
          />
        </div>
      </div>

      <AvailabilitiesMapPanel
        id="availabilities-map-panel"
        addressEntries={addressEntries}
        selectedGroup={selectedGroup}
        selectedUnit={selectedUnit}
        selectedPropertyId={selectedPropertyId}
        selectedUnitId={selectedUnitId}
        content={content.mapPanel}
        mapUnitRowContent={content.mapUnitRow}
        quickViewContent={content.quickView}
        availabilityLabels={content.availabilityLabels}
        onBack={() => {
          setSelectedPropertyId(null);
          setSelectedUnitId(null);
        }}
        onSelectProperty={(propertyId) => {
          setSelectedPropertyId(propertyId);
          setSelectedUnitId(null);
        }}
        onSelectUnit={(unitId) => {
          setSelectedUnitId(unitId);
          setMapFocusRequest((current) => current + 1);
        }}
      />
    </div>
  );
}
